import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const RegistrationForm = ({ tenantId, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    emergencyContact: '',
    emergencyPhone: '',
    membershipPlan: 'basic',
    billingCycle: 'monthly',
    startDate: new Date()?.toISOString()?.split('T')?.[0],
    specialAccommodations: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    onError('');

    try {
      // Validate form
      if (!formData?.fullName || !formData?.email) {
        throw new Error('Please fill in all required fields');
      }

      // Generate a temporary password
      const tempPassword = `Gym${Math.random()?.toString(36)?.slice(-8)}!`;

      // Step 1: Create user in Supabase Auth
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: tempPassword,
        options: {
          data: {
            full_name: formData?.fullName,
            phone: formData?.phone || '',
            role: 'member'
          },
          emailRedirectTo: `${window.location?.origin}/login`
        }
      });

      if (authError) throw authError;

      // Step 2: Wait for trigger to create user_profiles
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Step 3: Create tenant membership
      const { error: membershipError } = await supabase?.from('tenant_members')?.insert({
          tenant_id: tenantId,
          user_id: authData?.user?.id,
          role: 'member',
          membership_plan: formData?.membershipPlan,
          is_active: true
        });

      if (membershipError) throw membershipError;

      // Step 4: Create subscription record
      const endDate = new Date(formData?.startDate);
      if (formData?.billingCycle === 'monthly') {
        endDate?.setMonth(endDate?.getMonth() + 1);
      } else if (formData?.billingCycle === 'quarterly') {
        endDate?.setMonth(endDate?.getMonth() + 3);
      } else if (formData?.billingCycle === 'annual') {
        endDate?.setFullYear(endDate?.getFullYear() + 1);
      }

      const { error: subscriptionError } = await supabase?.from('member_subscriptions')?.insert({
          tenant_id: tenantId,
          user_id: authData?.user?.id,
          plan_name: formData?.membershipPlan,
          status: 'active',
          start_date: formData?.startDate,
          end_date: endDate?.toISOString()?.split('T')?.[0],
          billing_cycle: formData?.billingCycle
        });

      if (subscriptionError) throw subscriptionError;

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        emergencyContact: '',
        emergencyPhone: '',
        membershipPlan: 'basic',
        billingCycle: 'monthly',
        startDate: new Date()?.toISOString()?.split('T')?.[0],
        specialAccommodations: ''
      });

      onSuccess();
    } catch (err) {
      onError(err?.message || 'Failed to register member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-md border border-border">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
          <Icon name="UserPlus" size={24} color="var(--color-primary)" />
          Member Registration Form
        </h2>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Personal Information */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Full Name <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                value={formData?.fullName}
                onChange={(e) => handleChange('fullName', e?.target?.value)}
                placeholder="John Doe"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address <span className="text-destructive">*</span>
              </label>
              <Input
                type="email"
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
                placeholder="john@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Phone Number
              </label>
              <Input
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Emergency Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Contact Name
              </label>
              <Input
                type="text"
                value={formData?.emergencyContact}
                onChange={(e) => handleChange('emergencyContact', e?.target?.value)}
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Contact Phone
              </label>
              <Input
                type="tel"
                value={formData?.emergencyPhone}
                onChange={(e) => handleChange('emergencyPhone', e?.target?.value)}
                placeholder="+1 (555) 987-6543"
              />
            </div>
          </div>
        </div>

        {/* Membership Details */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Membership Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Membership Plan <span className="text-destructive">*</span>
              </label>
              <select
                value={formData?.membershipPlan}
                onChange={(e) => handleChange('membershipPlan', e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="basic">Basic - $29/month</option>
                <option value="premium">Premium - $59/month</option>
                <option value="elite">Elite - $99/month</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Billing Cycle <span className="text-destructive">*</span>
              </label>
              <select
                value={formData?.billingCycle}
                onChange={(e) => handleChange('billingCycle', e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly (3 months)</option>
                <option value="annual">Annual (12 months)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Start Date <span className="text-destructive">*</span>
              </label>
              <Input
                type="date"
                value={formData?.startDate}
                onChange={(e) => handleChange('startDate', e?.target?.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Special Accommodations */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Special Accommodations
          </label>
          <textarea
            value={formData?.specialAccommodations}
            onChange={(e) => handleChange('specialAccommodations', e?.target?.value)}
            placeholder="Any special requirements, medical conditions, or accessibility needs..."
            rows={3}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground resize-none"
          />
        </div>

        {/* Info Box */}
        <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg flex gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm text-foreground">
            <p className="font-semibold mb-1">Registration Process</p>
            <p className="text-muted-foreground">
              The member will receive a welcome email with login credentials and barcode. They can update their password after first login.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setFormData({
              fullName: '',
              email: '',
              phone: '',
              emergencyContact: '',
              emergencyPhone: '',
              membershipPlan: 'basic',
              billingCycle: 'monthly',
              startDate: new Date()?.toISOString()?.split('T')?.[0],
              specialAccommodations: ''
            })}
            disabled={loading}
            className="flex-1"
          >
            Clear Form
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={20} className="animate-spin" />
                Registering...
              </>
            ) : (
              <>
                <Icon name="UserPlus" size={20} />
                Register Member
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;